/**
 * 80mm thermal rolls have ~72mm of real printable width. If CSS content is
 * set to a full 80mm, browsers shrink-to-fit the page — that clips RTL text
 * on one edge and makes the logo/text look soft/damaged.
 *
 * Strategy: @page stays 80mm (paper), content box is 72mm with extra right
 * padding for RTL so nothing hits the physical roll edge.
 */
export const THERMAL_PAGE_WIDTH = '80mm';
export const THERMAL_CONTENT_WIDTH = '72mm';
/** CSS shorthand: top, right, bottom, left — more on the right for RTL. */
export const THERMAL_PRINT_PADDING = '2mm 5mm 2mm 3mm';

export const BILL_SIZES = {
  A4: {
    width: '210mm',
    minHeight: '297mm',
    height: '297mm',
    pageSize: 'A4 portrait',
    orientation: 'portrait',
  },
  // Half of A4: 148mm × 210mm portrait (A4 cut in half, standing).
  A5: {
    width: '148mm',
    minHeight: '210mm',
    height: '210mm',
    pageSize: 'A5 portrait',
    orientation: 'portrait',
  },
  // 80mm thermal receipt roll (content uses printable width, not full roll).
  Thermal: {
    width: THERMAL_CONTENT_WIDTH,
    minHeight: '120mm',
    height: 'auto',
    pageSize: `${THERMAL_PAGE_WIDTH} auto`,
    orientation: 'portrait',
  },
};

export function getBillDimensions(size = 'A4') {
  return BILL_SIZES[size] || BILL_SIZES.A4;
}

export function isThermalBill(pageSize = 'A4') {
  return pageSize === 'Thermal';
}

export function isCompactBill(pageSize = 'A4') {
  return pageSize === 'A5' || pageSize === 'Thermal';
}

/** Spacing/type tokens so A5 / Thermal fit header + rows + totals + footer. */
export function getBillLayout(pageSize = 'A4') {
  const thermal = isThermalBill(pageSize);
  const compact = isCompactBill(pageSize);
  return {
    compact,
    thermal,
    // Larger type/logo on thermal so print stays sharp (no tiny 7px text).
    headerHeight: thermal ? 52 : compact ? 52 : 100,
    logoSize: thermal ? 40 : compact ? 34 : 80,
    logoBorder: thermal ? 1.5 : compact ? 2 : 3,
    titleFontSize: thermal ? 13 : compact ? 14 : 26,
    billLabelFontSize: thermal ? 9 : compact ? 10 : 18,
    billLabelPadding: thermal ? '2px 6px' : compact ? '3px 8px' : '8px 20px',
    serialFontSize: thermal ? 9 : compact ? 10 : 16,
    headerPadding: thermal ? '4px 5px 4px 3px' : compact ? '4px' : '10px',
    infoPadding: thermal ? '4px 6px 4px 4px' : compact ? '5px 8px' : '15px 20px',
    infoFontSize: thermal ? 9 : compact ? 9 : 14,
    infoMarginBottom: thermal ? 2 : compact ? 2 : 5,
    tableWrapPadding: thermal ? '0 4px 0 2px' : compact ? '0 8px' : '0 20px',
    tableMarginTop: thermal ? 3 : compact ? 4 : 10,
    thPadding: thermal ? '3px 2px' : compact ? '3px 2px' : '8px',
    tdPadding: thermal ? '2px 2px' : compact ? '2px 2px' : '10px',
    thFontSize: thermal ? 8 : compact ? 8 : 13,
    tdFontSize: thermal ? 8 : compact ? 8 : 13,
    indexFontSize: thermal ? 8 : compact ? 8 : 14,
    colIndex: thermal ? 18 : compact ? 22 : 60,
    colSize: thermal ? 30 : compact ? 40 : 100,
    colDesign: thermal ? 28 : compact ? 40 : 90,
    colQty: thermal ? 24 : compact ? 28 : 80,
    colPrice: thermal ? 34 : compact ? 42 : 100,
    colTotal: thermal ? 38 : compact ? 50 : 120,
    totalsPadding: thermal ? '4px 6px 4px 4px' : compact ? '6px 8px' : '20px',
    totalsBoxPadding: thermal ? '4px 6px 4px 4px' : compact ? '4px 10px' : '15px 40px',
    totalsTitleSize: thermal ? 9 : compact ? 10 : 18,
    totalsValueSize: thermal ? 11 : compact ? 12 : 20,
    totalsBoxMinWidth: thermal ? 78 : compact ? 96 : 200,
    paidFontSize: thermal ? 9 : compact ? 9 : 14,
    signatureMarginTop: thermal ? 6 : compact ? 8 : 30,
    signatureLineMargin: thermal ? 8 : compact ? 10 : 30,
    signatureWidth: thermal ? 80 : compact ? 90 : 150,
    footerPadding: thermal ? '4px 5px 4px 3px' : compact ? '5px 8px' : '12px 20px',
    footerFontSize: thermal ? 7 : compact ? 7 : 12,
    footerAddressSize: thermal ? 6.5 : compact ? 6.5 : 11,
    footerMarginTop: thermal ? 3 : compact ? 2 : 20,
    footerAddressMaxWidth: thermal ? 130 : compact ? 180 : 400,
    footerAddressPadding: thermal ? '2px 8px 2px 5px' : compact ? '3px 10px 3px 8px' : '8px 30px 8px 15px',
    /** Empty filler rows — skip on thermal so type can stay readable. */
    emptyRowsTarget: thermal ? 0 : 11,
  };
}

export function getBillContainerStyle(pageSize = 'A4') {
  const dims = getBillDimensions(pageSize);
  const compact = isCompactBill(pageSize);
  const thermal = isThermalBill(pageSize);
  return {
    width: dims.width,
    minHeight: dims.minHeight,
    height: thermal ? 'auto' : dims.height,
    maxHeight: compact && !thermal ? dims.height : undefined,
    overflow: compact && !thermal ? 'hidden' : undefined,
    boxSizing: 'border-box',
    padding: thermal ? THERMAL_PRINT_PADDING : 0,
  };
}

function getPrintPageCss(dims, pageSize = 'A4') {
  const thermal = isThermalBill(pageSize);
  return `
    @media print {
      @page {
        size: ${dims.pageSize};
        margin: 0;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: ${dims.width} !important;
        max-width: ${dims.width} !important;
        ${thermal ? 'height: auto !important;' : `height: ${dims.height} !important;`}
        overflow: ${thermal ? 'visible' : 'hidden'} !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      body * { visibility: hidden; }
      .printable-bill, .printable-bill * { visibility: visible; }
      .printable-bill {
        position: absolute;
        left: 0;
        top: 0;
        width: ${dims.width} !important;
        max-width: ${dims.width} !important;
        min-height: ${dims.minHeight} !important;
        ${thermal ? 'height: auto !important; max-height: none !important; overflow: visible !important;' : `height: ${dims.height} !important; max-height: ${dims.height} !important; overflow: hidden !important;`}
        box-sizing: border-box !important;
        padding: ${thermal ? THERMAL_PRINT_PADDING : '0'} !important;
        background: white !important;
        box-shadow: none !important;
        page-break-after: avoid !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        /* Prevent browser shrink-to-fit blur on thermal rolls */
        zoom: 1 !important;
        transform: none !important;
      }
      .printable-bill img {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        image-rendering: auto;
        max-width: 100% !important;
      }
      button, .no-print { display: none !important; }
    }
  `;
}

/** Print a bill ref with A4, A5, or Thermal page size (keeps on-screen preview size in sync). */
export function printBillFromRef(billRef, pageSize = 'A4') {
  if (!billRef?.current) return;
  const dims = getBillDimensions(pageSize);
  const styleId = 'dynamic-bill-print-style';
  let styleEl = document.getElementById(styleId);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = getPrintPageCss(dims, pageSize);
  const printContents = billRef.current.innerHTML;
  const originalContents = document.body.innerHTML;
  document.body.innerHTML = `<div class="printable-bill">${printContents}</div>`;
  window.print();
  document.body.innerHTML = originalContents;
  window.location.reload();
}

export function getBillPrintCss(pageSize = 'A4') {
  return getPrintPageCss(getBillDimensions(pageSize), pageSize);
}
